/**
 * @file CallbackPage.tsx - OAuth Callback Handler
 *
 * This component handles the OAuth 2.0 authorization code callback.
 * After users authenticate with the external identity provider, they are
 * redirected back to this page with an authorization code in the URL.
 *
 * Flow:
 * 1. Extract authorization code from URL query parameters
 * 2. Exchange code for access token via handleAuthCallback()
 * 3. Verify and store token via login()
 * 4. Redirect user to original destination (room, shareable link, or home)
 *
 * The component uses a flag (hasAuthRun) to prevent duplicate execution
 * during React strict mode development remounting.
 *
 * Redirect handling:
 * - If user was joining a room: redirects to room URL
 * - If user was viewing a shared link: redirects to that link
 * - Otherwise: redirects to home page
 */
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "./AuthContext";
import { handleAuthCallback } from "./AuthContext";

/** Guard flag to prevent double execution in React strict mode */
let hasAuthRun = false;

const CallbackPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    const run = async () => {
      if (hasAuthRun) {
        return;
      }
      hasAuthRun = true;

      const params = new URLSearchParams(location.search);
      const code = params.get("code");

      if (!code) {
        console.error("No authorization code found in the URL");
        return;
      }

      try {
        const accessToken = await handleAuthCallback(code);
        await login(accessToken);

        // After login redirection
        const roomId = localStorage.getItem("room_id");
        const shareableLinkId = localStorage.getItem("shareable_link_id");

        if (roomId) {
          localStorage.removeItem("room_id");
          navigate(`/#room=${roomId}`);
        } else if (shareableLinkId) {
          localStorage.removeItem("shareable_link_id");
          navigate(`/#json=${shareableLinkId}`);
        } else {
          navigate("/");
        }
      } catch (error) {
        console.error("Auth callback failed:", error);
      }
    };

    run();
  }, [location.search, login, navigate]);

  return <div>Processing authentication...</div>;
};

export default CallbackPage;
